/**
 * Piece Stats Collector
 *
 * Extracts piece performance data from completed games and records it
 * to the PieceStatsCache. Called at game end for bot/AI games.
 *
 * Data flow:
 * 1. Parse fenHistory[0] to get initial army composition and column placements
 * 2. Scan final board state to find which pieces survived
 * 3. Update per-piece stats (win rate, survival, column performance, synergy)
 * 4. Cache marks dirty -> auto-flushes to Firebase
 */

import { GameState, PieceType, ISquare } from '../../zodiacEngine/types';
import { AIModel } from '../../zodiacEngine/AI/Core/AIVariants';
import {
  GameOutcomeData,
  createEmptyPieceStats,
  getSynergyKey,
  getMatchupKey,
  getAdjacentMatchupKey,
} from './PieceStatsTypes';
import { PieceStatsCache } from './PieceStatsCache';

/** Map FEN piece character (uppercase) to PieceType */
const CHAR_TO_PIECE: Record<string, PieceType> = {
  B: PieceType.Bear,
  C: PieceType.Crane,
  F: PieceType.Frog,
  G: PieceType.Goat,
  M: PieceType.Monkey,
  U: PieceType.Fungi,
  O: PieceType.Octopus,
  P: PieceType.Penguin,
  R: PieceType.Rhino,
  S: PieceType.Snake,
  T: PieceType.Tree,
  W: PieceType.Wolf,
  V: PieceType.Virus,
};

/** Known piece characters for quick lookup */
const PIECE_CHARS = new Set('BCFGMUOPRSTVWbcfgmuoprstvw');

/**
 * Extract the army composition from the first FEN in history.
 *
 * Zodiac FEN is column-first, bottom-up:
 * - Columns separated by '/'
 * - Within each column, row 0 (light start) is first, row 7 (dark start) is last
 * - Numbers represent empty squares
 * - Piece chars: uppercase = light, lowercase = dark
 * - Modifiers follow piece chars (direction arrows, status effects, king markers)
 * - '+' separates stacked pieces on the same square
 */
function extractInitialArmy(
  fenHistory: string[],
  isLight: boolean
): { pieces: PieceType[]; king: PieceType | undefined; placements: Map<PieceType, number> } {
  const empty = { pieces: [], king: undefined, placements: new Map() };
  if (!fenHistory || fenHistory.length === 0) return empty;

  let fen = fenHistory[0];

  // Strip turn indicator prefix
  if (fen.startsWith('d')) fen = fen.substring(1);

  // Strip metadata suffixes
  const bonusIdx = fen.indexOf('~');
  if (bonusIdx !== -1) fen = fen.substring(0, bonusIdx);
  const atIdx = fen.indexOf('@');
  if (atIdx !== -1) fen = fen.substring(0, atIdx);

  const columns = fen.split('/');
  const pieces: PieceType[] = [];
  const placements = new Map<PieceType, number>();
  let king: PieceType | undefined;

  // Target row: light starts on row 0, dark starts on row 7
  const targetRow = isLight ? 0 : 7;

  for (let col = 0; col < Math.min(columns.length, 5); col++) {
    const colStr = columns[col];
    let rowIndex = 0;
    let i = 0;

    while (i < colStr.length && rowIndex <= targetRow) {
      const char = colStr[i];

      // Number = empty squares
      if (char >= '0' && char <= '9') {
        rowIndex += parseInt(char);
        i++;
        continue;
      }

      // '+' = stacked piece on same square (don't advance row)
      if (char === '+') {
        i++;
        continue;
      }

      // Check if this is a piece character
      if (PIECE_CHARS.has(char)) {
        const isLightPiece = char === char.toUpperCase();
        const pieceType = CHAR_TO_PIECE[char.toUpperCase()];

        // Collect modifiers (everything until next piece char, number, '+', or '/')
        let isKingPiece = false;
        let j = i + 1;
        while (j < colStr.length) {
          const mod = colStr[j];
          if (PIECE_CHARS.has(mod) || (mod >= '0' && mod <= '9') || mod === '/') break;
          if (mod === '+') break;
          // Check for king markers
          if (mod === '\u265B' || mod === '\u2655' || mod === 'K' || mod === 'k') {
            isKingPiece = true;
          }
          j++;
        }

        // If this is the target row and the correct color, record it
        if (rowIndex === targetRow && isLightPiece === isLight && pieceType) {
          pieces.push(pieceType);
          placements.set(pieceType, col);
          if (isKingPiece) {
            king = pieceType;
          }
        }

        // Advance row unless next char is '+' (stacked on same square)
        if (j < colStr.length && colStr[j] === '+') {
          // Stay on same row for stacked piece
        } else {
          rowIndex++;
        }

        i = j;
      } else {
        // Unknown char, skip
        i++;
      }
    }
  }

  return { pieces, king, placements };
}

/**
 * Extract surviving pieces from the final board state.
 */
function extractSurvivors(board: ISquare[][], isLight: boolean): PieceType[] {
  const survivors: PieceType[] = [];
  for (const row of board) {
    for (const square of row) {
      for (const piece of square.Pieces) {
        if (piece.isLight === isLight && !piece.isShadow) {
          survivors.push(piece.type);
        }
      }
    }
  }
  return survivors;
}

export class PieceStatsCollector {
  /**
   * Record a completed game's results for an AI model's piece stats.
   *
   * Called at game end for:
   * - Bot games in Multiplayer (BotGameController)
   * - ChallengeAI games (local AI vs player)
   *
   * Non-blocking, non-fatal: errors are caught and logged.
   */
  static async recordGameResult(
    aiModel: AIModel,
    isLight: boolean,
    gameState: GameState,
    didWin: boolean
  ): Promise<void> {
    try {
      // 1. Extract initial army from first FEN
      const { pieces, king, placements } = extractInitialArmy(
        gameState.fenHistory, isLight
      );

      // Need at least 1 piece to record meaningful stats
      if (pieces.length === 0) {
        return;
      }

      // If king wasn't found in FEN markers, use the gameState king
      const actualKing = king || (isLight ? gameState.lightKing : gameState.darkKing);

      // 2. Extract survivors from final board
      const survivors = extractSurvivors(gameState.board, isLight);
      const survivorSet = new Set(survivors);

      // 3. Get or create stats from cache
      const cache = PieceStatsCache.getInstance();
      const stats = await cache.getStats(aiModel);

      // 4. Update totals
      stats.totalGames += 1;
      if (didWin) stats.totalWins += 1;

      // 5. Update per-piece stats
      for (const piece of pieces) {
        if (!stats.pieceStats[piece]) {
          stats.pieceStats[piece] = createEmptyPieceStats();
        }
        const ps = stats.pieceStats[piece];

        ps.gamesInArmy += 1;
        if (didWin) ps.winsInArmy += 1;
        if (survivorSet.has(piece)) ps.survivalCount += 1;

        // King stats
        if (piece === actualKing) {
          ps.gamesAsKing += 1;
          if (didWin) ps.winsAsKing += 1;
        }

        // Column stats
        const col = placements.get(piece);
        if (col !== undefined) {
          if (!ps.columnStats[col]) ps.columnStats[col] = { games: 0, wins: 0 };
          ps.columnStats[col].games += 1;
          if (didWin) ps.columnStats[col].wins += 1;
        }
      }

      // 6. Update pairwise synergy stats
      for (let i = 0; i < pieces.length; i++) {
        for (let j = i + 1; j < pieces.length; j++) {
          const key = getSynergyKey(pieces[i], pieces[j]);
          if (!stats.synergyStats[key]) stats.synergyStats[key] = { games: 0, wins: 0 };
          stats.synergyStats[key].games += 1;
          if (didWin) stats.synergyStats[key].wins += 1;
        }
      }

      // 7. Update laning matchup stats (our piece vs opponent piece in same column)
      if (!stats.matchupStats) stats.matchupStats = {};
      const opponentArmy = extractInitialArmy(gameState.fenHistory, !isLight);
      for (const [ourPiece, col] of placements) {
        for (const [oppPiece, oppCol] of opponentArmy.placements) {
          if (oppCol === col) {
            // 7a. Same-column matchup
            const mKey = getMatchupKey(ourPiece, oppPiece);
            if (!stats.matchupStats[mKey]) stats.matchupStats[mKey] = { games: 0, wins: 0 };
            stats.matchupStats[mKey].games += 1;
            if (didWin) stats.matchupStats[mKey].wins += 1;
          } else if (Math.abs(oppCol - col) === 1) {
            // 7b. Adjacent-column matchup (±1 column)
            const adjKey = getAdjacentMatchupKey(ourPiece, oppPiece);
            if (!stats.matchupStats[adjKey]) stats.matchupStats[adjKey] = { games: 0, wins: 0 };
            stats.matchupStats[adjKey].games += 1;
            if (didWin) stats.matchupStats[adjKey].wins += 1;
          }
        }
      }

      // 8. Mark dirty for batch write
      cache.markDirty(aiModel);

    } catch (error) {
      // Non-fatal: don't break game flow for stats recording failure
    }
  }

  /**
   * Force-flush any pending stats to Firebase.
   * Call on page unload or when you need immediate persistence.
   */
  static async flush(): Promise<void> {
    await PieceStatsCache.getInstance().flush();
  }
}
